import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Plot from 'react-plotly.js';
import './Planning.css';

const API_BASE_URL = "http://localhost:5001";
export default function Planning() {
    const { userId, experimentId, scenarioId } = useParams();

    const [scenarioData, setScenarioData] = useState(null);
    const [outputData, setOutputData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [selectedCharger, setSelectedCharger] = useState('');
    const [selectedVehicle, setSelectedVehicle] = useState('');

    const [chargerUtilData, setChargerUtilData] = useState(null);
    const [vehicleUtilData, setVehicleUtilData] = useState(null);
    const [costProjectionData, setCostProjectionData] = useState(null);

    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            setError(null);

            try {

                const scenarioUrl = `${API_BASE_URL}/users/${userId}/experiments/${experimentId}/${scenarioId}/scenario`;
                const resultsUrl = `${API_BASE_URL}/users/${userId}/experiments/${experimentId}/${scenarioId}/results`;


                const [scenarioResponse, outputResponse] = await Promise.all([
                    fetch(scenarioUrl),
                    fetch(resultsUrl)
                ]);

                if (!scenarioResponse.ok) {
                    throw new Error(`Server returned ${scenarioResponse.status} for scenario`);
                }
                if (!outputResponse.ok) {
                    throw new Error(`Server returned ${outputResponse.status} for results`);
                }

                const [scenarioData, outputData] = await Promise.all([
                    scenarioResponse.json(),
                    outputResponse.json()
                ]);

                setScenarioData(scenarioData);
                setOutputData(outputData);
                setLoading(false);
            } catch (fetchError) {
                console.error("Fetch failed:", fetchError);
                setError(fetchError.message);
                setLoading(false);
            }
        }
        fetchData();
    }, [userId, experimentId, scenarioId]);

    useEffect(() => {
        if (selectedCharger && outputData && outputData.metadata && outputData.metadata.chargers) {
            const chargerTypeData = outputData.metadata.chargers[selectedCharger];

            if (chargerTypeData) {
                // Get utilization data for the charger type
                const utilization = chargerTypeData.utilization || 0;
                const idle = 1 - utilization;

                setChargerUtilData({
                    labels: ['Utilization', 'Idle'],
                    values: [utilization * 100, idle * 100],
                    colors: ['#4CAF50', '#E0E0E0']
                });

            }
        }
    }, [selectedCharger, outputData]);

    // Effect to update vehicle utilization data when selection changes
    useEffect(() => {
        if (selectedVehicle && outputData && outputData.metadata && outputData.metadata.vehicles) {
            const vehicleTypeData = outputData.metadata.vehicles[selectedVehicle];

            if (vehicleTypeData) {

                // Extract utilization percentages for this vehicle type
                const operating = vehicleTypeData.operation_status_percentage || 0;

                const charging = vehicleTypeData.charging_status_percentage || 0;

                const idle = vehicleTypeData.idle_status_percentage || 0;


                setVehicleUtilData({
                    labels: ['Operating', 'Charging', 'Idle'],
                    values: [operating * 100, charging * 100, idle * 100],
                    colors: ['#2196F3', '#FFC107', '#E0E0E0']
                });
            }
        }
    }, [selectedVehicle, outputData]);

    useEffect(() => {
        if (outputData && outputData.metadata) {
            const projectionData = generateCostProjectionData(10);
            setCostProjectionData(projectionData);
        }
    }, [outputData]);

    if (loading) {
        return <div className="loading-container">Loading scenario...</div>;
    }

    if (error) {
        return (
            <div className="error-container">
                <h2>Error loading scenario</h2>
                <p>{error}</p>
                <p>Please check the URL and try again.</p>
            </div>
        );
    }

    if (!scenarioData) {
        return (
            <div className="no-data-container">
                <h2>No data available</h2>
                <p>No data available for scenario {scenarioId} in experiment {experimentId}.</p>
            </div>
        );
    }

    const generateCostProjectionData = (years = 10) => {
        if (!outputData || !outputData.metadata) return null;

        // Extract cost values, defaulting to 0 if undefined
        const vehicleCapex = outputData.metadata.total_vehicle_capex || 0;
        const chargerCapex = outputData.metadata.total_charger_capex || 0;

        const vehicleOperatingCost = outputData.metadata.total_vehicle_operating_cost || 0;
        const chargerOperatingCost = outputData.metadata.total_charger_operating_cost || 0;
        const powerCostPerYear = (outputData.metadata.peak_power_kW || 0) * 12; // Monthly cost * 12
        const energyCostPerYear = 365 * (outputData.metadata.total_price || 0);

        // Create data arrays for the stacked bar chart
        const xValues = Array.from({ length: years }, (_, i) => i + 1); // Years 1-10

        // Initialize arrays for each cost component
        const vehicleCapexValues = new Array(years).fill(0);
        const chargerCapexValues = new Array(years).fill(0);
        const vehicleOpexValues = new Array(years).fill(0);
        const chargerOpexValues = new Array(years).fill(0);
        const powerCostValues = new Array(years).fill(0);
        const energyCostValues = new Array(years).fill(0);
        const tcoValues = new Array(years).fill(0);

        // Set values for each year
        for (let i = 0; i < years; i++) {
            const year = i + 1;

            // CAPEX only in year 1
            if (year === 1) {
                vehicleCapexValues[i] = vehicleCapex;
                chargerCapexValues[i] = chargerCapex;
            }


            vehicleOpexValues[i] = vehicleOperatingCost;
            chargerOpexValues[i] = chargerOperatingCost;
            powerCostValues[i] = powerCostPerYear;
            energyCostValues[i] = energyCostPerYear;
            tcoValues[i] = vehicleCapex + chargerCapex +
                (vehicleOperatingCost + chargerOperatingCost + powerCostPerYear +
                    energyCostPerYear) * year;
        }
        let maxYValue = 0;
        for (let i = 0; i < years; i++) {
            const yearTotal = tcoValues[i];
            maxYValue = Math.max(maxYValue, yearTotal);

        }

        maxYValue = maxYValue * 1.1;
        return {
            xValues,
            vehicleCapexValues,
            chargerCapexValues,
            vehicleOpexValues,
            chargerOpexValues,
            energyCostValues,
            powerCostValues,
            tcoValues,
            maxYValue
        };
    };

    const chargerTypes = outputData?.metadata?.chargers ?
        Object.keys(outputData.metadata.chargers) : [];

    const vehicleTypes = outputData?.metadata?.vehicles ?
        Object.keys(outputData.metadata.vehicles)
            .filter(type => type.includes('_'))
            .filter((type, index, self) => self.indexOf(type) === index)
        : [];

    return (
        <div className="tables-section">
            <div className="heading-title">
                <h1>Planning</h1>
                <h4>{experimentId}: {scenarioId}</h4>
            </div>
            <div className="planning-table-container">
                <h2>Fleet Summary</h2>
                <table className="fleet-table-planning">
                    <thead>
                        <tr>
                            <th>Vehicle Type</th>
                            <th>Count</th>
                            <th>Battery Capacity</th>
                            <th>Energy Consumption (kWh)</th>
                            <th>Unit Cost</th>
                            <th>Operating Cost</th>
                        </tr>
                    </thead>
                    <tbody>
                        {scenarioData.vehicles &&
                            scenarioData.vehicles.map((vehicle, index) => (
                                <tr key={index}>
                                    <td>{vehicle.type}</td>
                                    <td>{vehicle.count}</td>
                                    <td>{vehicle.capacity}</td>
                                    <td>{vehicle.energy_consumption}</td>
                                    <td>{vehicle.unit_cost}</td>
                                    <td>{vehicle.operating_cost}</td>
                                </tr>
                            ))}
                    </tbody>
                </table>

                <br />
                <h2>Charger Summary</h2>
                <table className="fleet-table-planning">
                    <thead>
                        <tr>
                            <th>Charger Type</th>
                            <th>Count</th>
                            <th>Power(kW)</th>
                            <th>Unit Cost</th>
                            <th>Operating Cost</th>
                        </tr>
                    </thead>
                    <tbody>
                        {scenarioData.chargers &&
                            scenarioData.chargers.map((charger, index) => (
                                <tr key={index}>
                                    <td>{charger.type}</td>
                                    <td>{charger.count}</td>
                                    <td>{charger.power}</td>
                                    <td>{charger.unit_cost}</td>
                                    <td>{charger.operating_cost}</td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>
            <div className="utilization-charts-section">
                <h2>Utilization Analysis</h2>

                <div className="charts-container-planning">
                    <div className="chart-box">
                        <h3>Charger Utilization</h3>
                        <div className="selector-container">
                            <label htmlFor="charger-select">Select Charger:</label>


                            <select
                                id="charger-select"
                                value={selectedCharger}
                                onChange={(e) => setSelectedCharger(e.target.value)}
                                disabled={chargerTypes.length === 0}
                            >
                                <option value="">-- Select a charger type --</option>
                                {chargerTypes.map((type) => (
                                    <option key={type} value={type}>{type}</option>
                                ))}
                            </select>
                        </div>
                        <div className="chart-area">
                            {chargerUtilData ? (
                                <Plot
                                    data={[
                                        {
                                            type: 'pie',
                                            values: chargerUtilData.values,
                                            labels: chargerUtilData.labels,
                                            marker: {
                                                colors: chargerUtilData.colors
                                            },
                                            textinfo: 'label+percent',
                                            hoverinfo: 'label+percent',
                                            hole: 0.4
                                        }
                                    ]}
                                    layout={{
                                        title: `Charger ${selectedCharger} Utilization`,
                                        height: 400,
                                        width: 500,
                                        showlegend: true,
                                        legend: {
                                            orientation: 'h',
                                            y: -0.2
                                        }
                                    }}
                                    config={{
                                        responsive: true,
                                        displayModeBar: false
                                    }}
                                />
                            ) : (
                                <div className="no-chart-data">
                                    {selectedCharger ? 'Loading chart...' : 'Select a charger to view utilization data'}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="chart-box">
                        <h3>Vehicle Utilization</h3>
                        <div className="selector-container">
                            <label htmlFor="vehicle-select">Select Vehicle:</label>
                            <select
                                id="vehicle-select"
                                value={selectedVehicle}
                                onChange={(e) => setSelectedVehicle(e.target.value)}
                                disabled={vehicleTypes.length === 0}
                            >
                                <option value="">-- Select a vehicle type --</option>
                                {vehicleTypes.map((type) => (
                                    <option key={type} value={type}>{type}</option>
                                ))}
                            </select>
                        </div>
                        <div className="chart-area">
                            {vehicleUtilData ? (
                                <Plot
                                    data={[
                                        {
                                            type: 'pie',
                                            values: vehicleUtilData.values,
                                            labels: vehicleUtilData.labels,
                                            marker: {
                                                colors: vehicleUtilData.colors
                                            },
                                            textinfo: 'label+percent',
                                            hoverinfo: 'label+percent',
                                            hole: 0.4
                                        }
                                    ]}
                                    layout={{
                                        title: `Vehicle ${selectedVehicle} Utilization`,
                                        height: 400,
                                        width: 500,
                                        showlegend: true,
                                        legend: {
                                            orientation: 'h',
                                            y: -0.2
                                        }
                                    }}
                                    config={{
                                        responsive: true,
                                        displayModeBar: false
                                    }}
                                />
                            ) : (
                                <div className="no-chart-data">
                                    {selectedVehicle ? 'Loading chart...' : 'Select a vehicle to view utilization data'}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className="cost-projection-section">
                <h2>10-Year Cost Projection</h2>

                <div className="cost-chart-container">
                    {costProjectionData ? (
                        <Plot
                            data={[
                                // Vehicle CAPEX
                                {
                                    name: 'Vehicle Capital Cost',
                                    type: 'bar',
                                    x: costProjectionData.xValues,
                                    y: costProjectionData.vehicleCapexValues,
                                    marker: { color: '#4285F4' },
                                    hoverinfo: 'name+y',
                                },
                                // Charger CAPEX
                                {
                                    name: 'Charger Capital Cost',
                                    type: 'bar',
                                    x: costProjectionData.xValues,
                                    y: costProjectionData.chargerCapexValues,
                                    marker: { color: '#34A853' },
                                    hoverinfo: 'name+y',
                                },
                                // Vehicle OPEX
                                {
                                    name: 'Vehicle Operating Cost',
                                    type: 'bar',
                                    x: costProjectionData.xValues,
                                    y: costProjectionData.vehicleOpexValues,
                                    marker: { color: '#FBBC05' },
                                    hoverinfo: 'name+y',
                                },
                                // Charger OPEX
                                {
                                    name: 'Charger Operating Cost',
                                    type: 'bar',
                                    x: costProjectionData.xValues,
                                    y: costProjectionData.chargerOpexValues,
                                    marker: { color: '#EA4335' },
                                    hoverinfo: 'name+y',
                                },
                                // Power Cost
                                {
                                    name: 'Power Demand Cost',
                                    type: 'bar',
                                    x: costProjectionData.xValues,
                                    y: costProjectionData.powerCostValues,
                                    marker: { color: '#8E24AA' },
                                    hoverinfo: 'name+y',
                                },
                                // Energy Cost
                                {
                                    name: 'Energy Cost',
                                    type: 'bar',
                                    x: costProjectionData.xValues,
                                    y: costProjectionData.energyCostValues,
                                    marker: { color: '#00ACC1' },
                                    hoverinfo: 'name+y',
                                },
                                {
                                    name: 'Total Cost of Ownership',
                                    type: 'scatter',
                                    x: costProjectionData.xValues,
                                    y: costProjectionData.tcoValues,
                                    marker: 'lines+markers',
                                    hoverinfo: 'name+y',
                                    line: {
                                        color: '#000000',
                                        width: 3,
                                        dash: 'solid',
                                    },
                                    marker: {
                                        color: '#000000',
                                        size: 8,
                                        symbol: 'circle',
                                    },
                                    hoverinfo: 'name+y',
                                    hovertemplate: '%{y:$,.0f}<extra>TCO</extra>',
                                    yaxis: 'y2',
                                },
                            ]}
                            layout={{
                                title: 'Cost Projection Over 10 Years',
                                barmode: 'stack',
                                xaxis: {
                                    title: 'Year',
                                    tickvals: costProjectionData.xValues,
                                    ticktext: costProjectionData.xValues.map(year => `Year ${year}`),
                                },
                                yaxis: {
                                    title: 'Cost ($)',
                                    hoverformat: ',.2f',
                                    autorange: true,
                                    rangemode: 'tozero',
                                },
                                yaxis2: {
                                    title: 'Total Cost of Ownership ($)',
                                    titlefont: { color: '#000000' },
                                    tickfont: { color: '#000000' },
                                    overlaying: 'y',
                                    side: 'right',
                                    showgrid: false,
                                    rangemode: 'tozero',
                                },
                                legend: {
                                    orientation: 'h',
                                    y: -0.2,
                                },
                                hovermode: 'closest',
                                height: 500,
                                autosize: true,
                                margin: { l: 80, r: 40, b: 120, t: 80 },
                            }}
                            config={{
                                responsive: true,
                                displayModeBar: false,
                            }}
                        />
                    ) : (
                        <div className="no-chart-data">Loading cost projection data...</div>
                    )}
                </div>

                <div className="cost-projection-note">
                    <p>
                        <strong>Note:</strong> Capital costs (vehicle and charger) are one-time investments shown in year 1.
                        All other costs accumulate annually. This projection helps visualize the total cost of ownership over time.
                    </p>
                </div>
            </div>

        </div>
    );
}